/**
 * Move selected media to another event.
 *
 * A "move" relocates three objects per item — original, thumbnail, and EXIF
 * sidecar — by copying to the destination key then deleting the source. The
 * backend `/bulk-operations {operation:'move'}` performs copy+delete per pair
 * (requires the updated Lambda).
 *
 * On success we invalidate the affected file lists, the events list, and the
 * cached signed URLs for the old keys.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { bulkMove, type MovePair, type BulkResult } from '../lib/apiClient.ts';
import {
  buildUploadKeys,
  basename,
  toThumbnailKey,
  toSidecarKey,
  sanitizeEventName,
} from '../lib/keys.ts';
import { UNCATEGORIZED_EVENT } from '../config/awsConfig.ts';
import { invalidateSignedUrl } from './useSignedUrl.ts';
import { timestampFromKey } from '../lib/keys.ts';

export interface MoveArgs {
  /** Original keys to move. */
  originalKeys: string[];
  /** Destination event name. */
  destinationEvent: string;
}

/** Build the destination original key for a source key under a new event. */
function destinationOriginalKey(sourceOriginalKey: string, destEvent: string): string {
  const name = basename(sourceOriginalKey);
  // Preserve the existing <ts>_<rest> name; only the event folder changes.
  const ts = timestampFromKey(sourceOriginalKey) ?? Date.now();
  const rest = name.replace(/^\d{10,}_/, '');
  return buildUploadKeys(destEvent, rest, ts).originalKey;
}

/** Expand one original-key move into the 3 underlying object moves. */
export function expandMovePairs(sourceOriginalKey: string, destEvent: string): MovePair[] {
  const destOriginal = destinationOriginalKey(sourceOriginalKey, destEvent);
  return [
    { source: sourceOriginalKey, destination: destOriginal },
    { source: toThumbnailKey(sourceOriginalKey), destination: toThumbnailKey(destOriginal) },
    { source: toSidecarKey(sourceOriginalKey), destination: toSidecarKey(destOriginal) },
  ];
}

export function useMove(sourceEvent: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ originalKeys, destinationEvent }: MoveArgs): Promise<BulkResult[]> => {
      const dest = sanitizeEventName(destinationEvent) || UNCATEGORIZED_EVENT;
      const pairs = originalKeys.flatMap((key) => expandMovePairs(key, dest));
      return bulkMove(pairs);
    },
    onSuccess: async (_results, { originalKeys, destinationEvent }) => {
      // Drop cached signed URLs for the moved source keys (now 404).
      for (const key of originalKeys) {
        invalidateSignedUrl(key);
        invalidateSignedUrl(toThumbnailKey(key));
      }
      const dest = sanitizeEventName(destinationEvent) || UNCATEGORIZED_EVENT;
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['files', sourceEvent] }),
        queryClient.invalidateQueries({ queryKey: ['files', dest] }),
        queryClient.invalidateQueries({ queryKey: ['events'] }),
      ]);
    },
  });
}
